import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInRouterContext, useSearchParams } from 'react-router-dom';
import { api, LedgerView } from '../../utils/api';
import HtmlViewer from './HtmlViewer';
import Pagination, { getPageSize } from '../Pagination';
import PeakToolbar, { BarAction } from '../PeakToolbar';
import type { AwCtx } from './ctx';
import { EntryDetailDialog } from './JournalBook';
import { NoCell } from './listCells';
import { SourceDocOpen, type SourceRef } from './sourceDoc';
import { baht, btn, card, dmy, errBox, inp, note, num, openBlob, pillGrey, sectionHead, table, td, th } from './ui';
import { Loader, chip, chipOn, emptyLine } from './dialog';
import { SortTh, sortArgs, useListSort } from './listSort';   // batch 12 tail T3 (FS-11): level 1's headers sort, inside the class groups
// The chart's own badges (batch 5 round 6): the SAME component the chart of accounts draws a type
// with, so an account wears one look on both screens and neither works out what a word means.
import { TypeBadge, ControlBadge } from '../Bookkeeping/AccountTypeBadge';
import type { ChartKind } from '../../utils/api/coatype';

/**
 * COA-B-1 · B-2 · B-3 · B-6 · B-7 · B-9 · B-10 — THE GENERAL LEDGER AS A SCREEN (owner 2026-10-19).
 *
 * The customer's complaint has an exact landing point. Until this file the `general_ledger` menu
 * rendered the STATUTORY PRINT inline (`HtmlViewer base="ledger-books" path="general-ledger"`, in
 * panes/Accounting.tsx), and that print lays every account's movements out one under another — 273
 * accounts of them on production — with nothing to click. What an accountant wants first is one row
 * per account with four figures on it, and only then the movements of the account they picked.
 *
 * So the menu now opens LEVEL 1 (one row per account, grouped under the five classes) and clicking
 * a code or a name opens LEVEL 2 (that account's own movements, with a running balance). The same
 * demotion JournalBook.tsx did to the journal PDF: on screen first, the print one click away.
 *
 * ── ⛔ THE STATUTORY PRINT IS NOT TOUCHED, NOT CHANGED, AND NOT REPLACED ────────────────────────
 * `/api/ledger-books/general-ledger`, `/trial-balance` and `/subsidiary/:kind` render บัญชีแยกประเภท
 * and its subsidiary ledgers — the books the DBD reads. This screen stands BESIDE them: their
 * buttons are in the toolbar under "the statutory book", and the same print HTML this menu used to
 * show is still reachable, on screen, from the last row of that menu. ⛔ No filter of this screen
 * reaches those routes — `ledgerBookUrl` takes the period and nothing else — because a statutory
 * book holding only what somebody had filtered to is a false document.
 *
 * ── ⛔ ONE ALGORITHM, ONE PLACE ──────────────────────────────────────────────────────────────────
 * Not one figure on this screen is computed here. The four totals, the running balance, the opening
 * balance and the counts are the SERVER's (server/src/services/ledgerAccounts.ts), and the six exits
 * — list, detail, and the two export formats of each — are fed by ONE filter object through ONE
 * query builder (`api.ledgerAcctQs`, the `jnlViewQs` pattern). The screen adds no sum of its own,
 * not even a per-class subtotal: a subtotal computed here would appear on the screen and not in the
 * file taken from it, which is the one difference an export must never have.
 *
 * ── ⛔ READ-ONLY, AND NOT BY OMISSION ───────────────────────────────────────────────────────────
 * A ledger is the sum of what was posted. There is no Save on this screen, no disabled Save, and no
 * editable field: the way to change a balance is to post or reverse an entry, which happens in
 * Accounting › Journal entries. Said in words at the foot, where somebody looking for a Save will
 * read it.
 */

/** The five RECORDED classes, labelled. ⛔ These are LABELS for a value the server returned — the
 *  class menu itself is built from GET /account-classes, so this screen cannot invent a sixth, and
 *  a class with no label here prints itself (the StatusTag precedent in JournalBook.tsx) rather than
 *  being renamed to one that happens to have a translation. ⛔ And nothing anywhere in this file
 *  derives a class from a code's digits or an account's name: `class` is the one recorded answer.
 *  The account's GROUP and TYPE (batch 5 round 6) are shown beside it as the words the chart stores
 *  — never translated, never read to decide anything here — with the badge the SERVER's `kind` gives
 *  the type. */
const CLASS_KEY: Record<string, string> = {
  asset: 'aw_gl_cls_asset', liability: 'aw_gl_cls_liability', equity: 'aw_gl_cls_equity',
  income: 'aw_gl_cls_income', expense: 'aw_gl_cls_expense',
};

/** What an opening balance of 0 MEANS, keyed off the server's own basis code.
 *
 *  ⛔ A bare 0 is a claim these books cannot support. On production all five accounting periods
 *  carry is_first_period = 1 with an empty carried_forward_json and no opening entry posted, so the
 *  0 in that column means "nothing was ever recorded before this window" — not "we added up what
 *  came before and it cancelled out". The server returns the facts (basis · isFirstPeriod ·
 *  priorPeriod · carriedForward · openingEntryPosted · priorLines) and its own English sentence;
 *  this map is the three-language version of the sentence, and the sentence is printed BESIDE the
 *  figure, never absorbed into it. */
const OPENING_KEY: Record<string, string> = {
  'prior-lines': 'aw_gl_open_prior_lines',
  'first-period-no-carry-forward': 'aw_gl_open_first_period',
  'no-prior-period': 'aw_gl_open_no_prior',
  'carried-forward-not-posted': 'aw_gl_open_carried_not_posted',
  // ⛔ A POSTED OPENING ENTRY GETS ITS OWN SENTENCE, because the first-period one denied it. Until
  // 2026-10-20 a posted period_open entry left the basis on 'first-period-no-carry-forward', whose
  // sentence says "no opening entry has been posted" while openingEntryPosted: true sat in the same
  // payload. (Since 2026-11-02 this view leaves a system opening entry out of its figures, the owner's ruling for the trial balance and an
  // engineering call for this screen: services/ledgerAccounts.ts SYSTEM_OPENING_IDS. The sentence says so, and that the statutory print differs by it.)
  'opening-entry-posted': 'aw_gl_open_entry_posted',
};

/** ⛔ A DATE RANGE IS NOT AN ACCOUNTING PERIOD, so it does not get a period's sentence.
 *
 *  services/ledgerAccounts openingFacts() forces `isFirstPeriod` false in range mode and looks for
 *  no prior period at all, so a range with nothing posted before it falls down the ladder to
 *  basis 'no-prior-period' — whose PERIOD sentence (aw_gl_open_no_prior) ends with a warning that
 *  the first-period flag is missing or wrong. In a date-range window there is no flag to be wrong,
 *  and on production all five periods carry is_first_period = 1, so every clause of that warning
 *  would be false — and the first custom range an operator opens produces it, because the range
 *  boxes are pre-filled with the period's own dates.
 *
 *  The server already says the true thing in `opening.reason` (and both exports print that, not a
 *  key), so this map is only that sentence in three languages. Range mode can reach three bases by
 *  construction — 'prior-lines', whose wording fits either window, this one, and since 2026-11-02 a
 *  system opening entry in the range with nothing before it, which reads the same (the line under it
 *  says what was left out) — because `carried` is 0 and `isFirst` is false there. An unlisted code
 *  still falls through to `opening.reason` below, as an unlisted period code always did. */
const RANGE_OPENING_KEY: Record<string, string> = {
  'prior-lines': 'aw_gl_open_prior_lines',
  'no-prior-period': 'aw_gl_open_range_no_prior',
  'opening-entry-posted': 'aw_gl_open_range_no_prior',
};

// ── What the two routes answer, as they answer it ───────────────────────────────────────────────
// Every money field is INTEGER SATANG, the ledger's own unit (ui.ts `baht` is the one formatter).
export type GlOpening = {
  basis: string; isFirstPeriod: boolean;
  priorPeriod: { id: string; from: string; to: string; status: string } | null;
  carriedForward: number; openingEntryPosted: boolean; openingEntryLines: number; openingEntryDr: number; openingEntryCr: number;
  priorLines: number; dateOutliers: number; reason: string;
};
export type GlOffChart = { lines: number; dr: number; cr: number; reason: string };
export type GlWindow = { basis: string; clientId: string; periodId: string; from: string; to: string; status: string };
export type GlAccountRow = {
  accountId: string; code: string; nameTh: string; nameEn: string;
  class: string; control: string; active: boolean; normalSide: 'dr' | 'cr';
  /** The chart's own words ('' = none given) and the server's kind for the type ('' = not classified). */
  group: string; type: string; kind: ChartKind;
  opening: number; dr: number; cr: number; closing: number;
  openingNormal: number; closingNormal: number;
  lines: number; entries: number;
  /** b13 S2 (COA-B-11): where the account sits in the chart's tree — the SERVER's walk (services/coaRules.ts
   *  chartTreeWalk): the parent it sits under and its code ('' at the top), the depth, its live sub-accounts, and a
   *  stored tree problem ('' = none). `rollup` = the account AND every account under it (null on a leaf); the figures
   *  above stay its OWN — the totals add those, so every posted line is counted once. */
  parentId?: string; parentCode?: string; depth?: number; children?: number; descendants?: number; treeProblem?: string;
  rollup?: null | { opening: number; dr: number; cr: number; closing: number; openingNormal: number; closingNormal: number; lines: number };
};
export type GlSummary = {
  window: GlWindow; entity: string; opening: GlOpening; offChart: GlOffChart;
  rows: GlAccountRow[];
  totals: { accounts: number; opening: number; dr: number; cr: number; closing: number; balanced: boolean; coversWholeChart: boolean };
  counts: { chart: number; shown: number; hidden: number; withMovement: number };
  label: string;
  /** b13 S1 (COA-B-6): the Group menu's choices — every group word this chart uses with its count, over the whole
   *  chart whatever the filter, '' (not grouped) last. The server's list: the screen never builds one of its own. */
  groupOptions?: { group: string; accounts: number }[];
  /** b13 S2: the tree over the whole chart; `flat` = a column sort is on (no indentation is drawn). */
  tree?: { parents: number; maxDepth: number; flat: boolean };
};
export type GlLineRow = {
  accountId: string; code: string; nameTh: string; nameEn: string; class: string;
  entryId: string; lineId: string; entryDate: string; docNo: string; journalNo: string; docRef: string; source?: SourceRef;
  narration: string; book: string; bookTh: string; pageNo: number | null; partnerName: string;
  dr: number; cr: number; balance: number; balanceNormal: number;
  originKind: string; originId: string;
};
export type GlDetail = {
  window: GlWindow; entity: string; opening: GlOpening; offChart: GlOffChart;
  account: { accountId: string; code: string; nameTh: string; nameEn: string; class: string; control: string; normalSide: 'dr' | 'cr' } | null;
  openingSatang: number; openingNormal: number; openingOf: Record<string, number>;
  rows: GlLineRow[];
  totals: { lines: number; entries: number; dr: number; cr: number; closing: number; closingNormal: number; accounts: number };
  page: number; limit: number; total: number; label: string;
};

/** Which account level 2 is reading, carried out of level 1's own row so the header can be drawn
 *  before the first byte of the detail arrives. */
type Picked = { accountId: string; code: string; nameTh: string; nameEn: string; class: string;
  /** b13 S2: the row's roll-up and how many accounts sit under it — level 2's head says what the subtree adds. */
  rollup?: GlAccountRow['rollup']; descendants?: number };

const err = (e: any): string => String(e?.message || e);
/** The page's own scroll offset, however this browser reports it (COA-B-9). */
const pageY = (): number => Number(window.scrollY || document.documentElement?.scrollTop || 0);

/** The opening balance, said in words beside the figure. Used at the head of level 1 and again at
 *  the head of level 2, because each is read on its own. */
export function OpeningNote({ o, window: w, entrySaid }: { o: GlOpening; window: GlWindow | null;
  /** The page says what a system opening entry left out for itself (the trial balance's footnote, with the amount). */
  entrySaid?: boolean }) {
  const { t } = useTranslation();
  // ⛔ The WINDOW decides which sentence, not the basis code alone: the same code means a different
  // thing in a period and in a date range (see RANGE_OPENING_KEY).
  const range = !!w && w.basis === 'range';
  const k = range ? RANGE_OPENING_KEY[o.basis] : OPENING_KEY[o.basis];
  return (
    <div style={{ ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <i className="ti ti-info-circle" style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} aria-hidden />
      <div style={note}>
        <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>{t('aw_gl_opening_title')}: </span>
        {/* An unfamiliar basis code prints the SERVER's own sentence rather than a translated guess. */}
        {k ? t(k, { from: dmy(w ? w.from : ''), lines: o.priorLines, carried: o.carriedForward, openLines: o.openingEntryLines, dr: baht(o.openingEntryDr || 0), cr: baht(o.openingEntryCr || 0) }) : o.reason}
        {/* Period mode selects by period_id, as the statutory ledger does, so an entry dated outside
            its own period is still in it. Counted and said, never re-filed by date. */}
        {o.dateOutliers > 0 && <> {t('aw_gl_date_outliers', { count: o.dateOutliers })}</>}
        {/* (2026-11-02) Earlier lines form the opening here, so that sentence is the one printed, and a posted system opening
            entry still has to be said: it is the difference from the statutory book. A date range says it whichever sentence it reads, and without
            "differs by its amount": the statutory book covers the whole period (review 2026-11-02). */}
        {!entrySaid && o.openingEntryPosted && range && <span data-gl-open-left-out="range"> {t('aw_gl_open_entry_left_out_range', { openLines: o.openingEntryLines, dr: baht(o.openingEntryDr || 0), cr: baht(o.openingEntryCr || 0) })}</span>}
        {!entrySaid && o.openingEntryPosted && !range && o.basis === 'prior-lines' && <span data-gl-open-left-out="period"> {t('aw_gl_open_entry_left_out', { openLines: o.openingEntryLines, dr: baht(o.openingEntryDr || 0), cr: baht(o.openingEntryCr || 0) })}</span>}
      </div>
    </div>
  );
}

/** Posted money that no row of this screen can carry. ⛔ Reported, never absorbed: the statutory
 *  trial balance still prints these lines, so a screen that said nothing would be quietly short of
 *  the book the firm files. */
function OffChartNote({ o }: { o: GlOffChart }) {
  const { t } = useTranslation();
  if (!o.lines) return null;
  return (
    <div style={{ ...errBox, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <i className="ti ti-alert-triangle" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden />
      <div>{t('aw_gl_offchart', { count: o.lines, dr: baht(o.dr), cr: baht(o.cr) })}</div>
    </div>
  );
}

/** Why there is no Save — said where somebody hunting for one will read it. */
export function ReadOnlyNote() {
  const { t } = useTranslation();
  return (
    <div style={{ ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <i className="ti ti-lock" style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} aria-hidden />
      <div style={note}>{t('aw_gl_readonly')}</div>
    </div>
  );
}

/**
 * LEVEL 2 — one account's movements (COA-B-7).
 *
 * ⛔ THE RUNNING BALANCE IS THE SERVER'S. `row.balance` is the account's opening plus every line up
 * to and including that one, struck in services/ledgerAccounts.ts over the whole ordered set before
 * paging — so page 3's first row carries what it has after pages 1 and 2, and the screen, the
 * spreadsheet and the printout cannot each arrive at a different number. Nothing here accumulates.
 */
function AccountLines({ ctx, picked, view, onBack, onExport, busy, exportErr }: {
  ctx: AwCtx; picked: Picked; view: LedgerView; onBack: () => void;
  onExport: (k: string, run: () => Promise<any>) => void; busy: string;
  /** A failed download is raised by the parent (one `busy` for the whole screen), so it has to be
   *  SHOWN here or it would be shown nowhere — the level-1 error box is not on this screen. */
  exportErr: string;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [d, setD] = useState<GlDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [e, setE] = useState('');
  // COA-B-8's near half: which voucher is open in the full-screen window. '' = none. The dialog is
  // JournalBook's own — one voucher window in this workspace, not a second one.
  const [entry, setEntry] = useState('');

  const one: LedgerView = useMemo(() => ({ ...view, accountId: picked.accountId }), [view, picked.accountId]);
  useEffect(() => { setPage(1); }, [one]);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.ledgerAccountLines(one, page, limit)
      .then((x: GlDetail) => { if (alive) { setD(x); setE(''); } })
      .catch((x: any) => { if (alive) { setD(null); setE(err(x)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [one, page, limit, ctx.reload]);

  const rows = d?.rows || [];
  const shownFrom = d && d.total ? (d.page - 1) * (d.limit || limit) + 1 : 0;
  const shownTo = d ? shownFrom + rows.length - 1 : 0;
  const clsLabel = CLASS_KEY[picked.class] ? t(CLASS_KEY[picked.class]) : (picked.class || '—');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <button onClick={onBack} style={btn}><i className="ti ti-arrow-left" aria-hidden /> {t('aw_gl_back')}</button>
        {/* COA-B-7: the account's code and name in the header, so the page says whose ledger it is. */}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
            {picked.code} · {picked.nameTh || picked.nameEn}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            {picked.nameEn && picked.nameTh ? `${picked.nameEn} · ` : ''}{clsLabel}
            {d ? ` · ${dmy(d.window.from)} – ${dmy(d.window.to)}` : ''}
          </div>
          {/* b13 S2 (B13-21): level 2 stays the account's OWN lines (its running balance keeps one meaning); for a parent
              the head says what its subtree adds — the level-1 row's roll-up, the server's figure. */}
          {!!picked.rollup && (
            <div data-d-rollup style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>
              ∑ {t('aw_gl_d_rollup', { count: picked.descendants || 0, closing: baht(picked.rollup.closing) })}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
        {/* COA-B-10, this account alone. Both formats, the SAME filter object the list above used. */}
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginTop: 7 }}>
          {t('aw_gl_export_account')}
        </span>
        {/* ⛔ Labelled 'Excel' / 'PDF' under the group word 'This account', not 'Excel — this view':
            the level-1 menu carries the view's own two files and a reader must not have to work out
            which of the four they are clicking. Both buttons spin, the house's download rule. */}
        <button title={t('aw_gl_export_account_title')} disabled={!!busy}
          onClick={() => onExport('d-xlsx', () => api.ledgerAccountLinesXlsx(one))}
          style={{ ...btn, opacity: busy === 'd-xlsx' ? 0.6 : 1 }}>
          <i className={busy === 'd-xlsx' ? 'ti ti-loader' : 'ti ti-file-spreadsheet'} aria-hidden /> {t('aw_jl_view_xlsx')}
        </button>
        <button title={t('aw_gl_export_account_title')} disabled={!!busy}
          onClick={() => onExport('d-pdf', () => api.ledgerAccountLinesPdf(one))}
          style={{ ...btn, opacity: busy === 'd-pdf' ? 0.6 : 1 }}>
          <i className={busy === 'd-pdf' ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> {t('aw_jl_view_pdf')}
        </button>
      </div>

      {e && <div style={errBox}>{e}</div>}
      {!!exportErr && <div style={errBox}>{exportErr}</div>}
      {d && <OpeningNote o={d.opening} window={d.window} />}
      {d && <OffChartNote o={d.offChart} />}

      {d && d.total > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('aw_gl_d_showing', { from: shownFrom, to: shownTo, total: d.total })}
          {' · '}{t('aw_gl_d_entries', { count: d.totals.entries })}
          {/* The running balance does not restart on a page boundary — said out loud, because a
              reader who assumed it did would read page 2's first figure as a subtotal. */}
          {d.total > rows.length ? ` · ${t('aw_gl_d_absolute')}` : ''}
        </div>
      )}

      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {!d && loading ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_gl_d_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_col_date')}</th>
                <th style={th}>{t('aw_jl_journal_no')}</th>
                <th style={th}>{t('aw_gl_d_source')}</th>
                <th style={th}>{t('aw_gl_d_journal')}</th>
                <th style={th}>{t('aw_gl_d_partner')}</th>
                <th style={th}>{t('aw_gl_d_narration')}</th>
                {/* ⛔ 'Debit' / 'Credit', not level 1's 'Period debit' / 'Period credit': these cells
                    are ONE movement's own two sides, not a window's total, and the detail export
                    heads the same two columns the same way. */}
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_jl_detail_dr')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_jl_detail_cr')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_gl_d_balance')}</th>
              </tr>
            </thead>
            <tbody>
              {/* The opening, in the balance column where the running balance starts from it. Only
                  on page 1: on a later page the figure above the first row is the previous page's
                  last balance, which is NOT an opening and must not be labelled as one. */}
              {d.page === 1 && (
                <tr>
                  <td style={{ ...td, fontWeight: 700, color: 'var(--text-secondary)' }} colSpan={8}>{t('aw_gl_d_opening_row')}</td>
                  <td style={{ ...num, fontWeight: 700 }}>{baht(d.openingSatang)}</td>
                </tr>
              )}
              {/* COA-B-8's near half: a movement opens the voucher behind it.
                  (EXCEPTION, owner 2026-11-02, "Modification — Trial Balance Module", the chain Trial Balance, General Ledger, Journal Entries: from its Journal No.
                  and its entry number, not from the row, and the source number is the second link. A trial balance line reads CPJ-202611-0001, and landing here on an
                  account's ledger it must not read as something else. An older entry reads "—" and its entry number under it, as the journal list and the trial balance do.) */}
              {rows.map(r => (
                <tr key={r.lineId} data-gl-line={r.lineId}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(r.entryDate)}</td>
                  <NoCell
                    no={<button type="button" data-gl-open-entry={r.entryId} onClick={() => setEntry(r.entryId)} title={t('aw_gl_open_voucher')} style={linkBtn}>{r.journalNo || '—'}</button>}
                    sub={r.docNo ? <button type="button" data-gl-open-entry-doc={r.entryId} onClick={() => setEntry(r.entryId)} title={t('aw_gl_open_voucher')} style={{ ...linkBtn, fontWeight: 400, color: 'inherit' }}>{r.docNo}</button> : undefined} />
                  {/* ⛔ The SOURCE document's number, never the voucher's own: filling this cell with the entry
                      number is how a reader reconciles to the wrong paper. The resolver's where it has one, and it
                      opens that document; else what the line itself says, unless that is the voucher's own number. */}
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {r.source?.docNo
                      ? <SourceDocOpen clientId={ctx.clientId} s={r.source} label={r.source.docNo} places />
                      : (r.docRef && r.docRef !== r.docNo ? r.docRef : <span style={{ color: 'var(--text-tertiary)' }}>—</span>)}
                  </td>
                  {/* The journal's own statutory name as the server's country pack gave it — never
                      one translated or invented here. */}
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.bookTh || r.book}</td>
                  <td style={td}>{r.partnerName}</td>
                  <td style={td}>{r.narration}</td>
                  <td style={num}>{r.dr ? baht(r.dr) : ''}</td>
                  <td style={num}>{r.cr ? baht(r.cr) : ''}</td>
                  <td style={{ ...num, fontWeight: 600 }}>{baht(r.balance)}</td>
                </tr>
              ))}
            </tbody>
            {/* The window's own totals — the server's three figures, reported, not recomputed. */}
            <tfoot>
              <tr>
                <td style={{ ...td, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }} colSpan={6}>
                  {t('aw_gl_d_total', { count: d.totals.lines })}
                </td>
                <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }}>{baht(d.totals.dr)}</td>
                <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }}>{baht(d.totals.cr)}</td>
                <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }}>{baht(d.totals.closing)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
      {d && <Pagination page={page} total={d.total} limit={d.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <ReadOnlyNote />
      {/* COA-B-8's near half: the voucher behind a movement, in the window JournalBook already
          ships. Read-only, so no `dirty` flag reaches it. */}
      {entry && <EntryDetailDialog entryId={entry} onClose={() => setEntry('')} />}
    </div>
  );
}

/**
 * The two levels, and the one state they share (COA-B-9).
 *
 * ⛔ COMING BACK COSTS NOTHING. The period, the date range, the class, both toggles, the search box
 * AND THE SCROLL POSITION are all held here, above the level that comes and goes, so returning from
 * an account lands exactly where the reader left — not at the top of a 273-row list they then have
 * to find their place in again. The scroll offset is read on the way in and WRITTEN BACK in a layout
 * effect on the way out, after the rows are in the DOM and before the browser paints; level 1's data
 * is never discarded while level 2 is open, so there is something to scroll to at that moment.
 */
export function LedgerAccountsView({ ctx }: { ctx: AwCtx }) {
  const inRouter = useInRouterContext();
  return inRouter ? <LedgerAccountsRouted ctx={ctx} /> : <LedgerAccountsBody ctx={ctx} link={null} />;
}

/** (Owner 2026-11-02, "Modification — Trial Balance Module") The trial balance's account code lands here with the account and, for a month, a
 *  quarter or custom dates, its window in the URL (glAccount / glFrom / glTo): level 2 opens on them, and the URL is cleared so a
 *  reload opens the list. The way JournalPane takes its Record-by-hand link. Without them nothing here differs. */
function LedgerAccountsRouted({ ctx }: { ctx: AwCtx }) {
  const [params, setParams] = useSearchParams();
  const account = params.get('glAccount') || '', glFrom = params.get('glFrom') || '', glTo = params.get('glTo') || '';
  const [link] = useState(() => (account ? { accountId: account, from: glFrom, to: glTo } : null));
  useEffect(() => {
    if (!account) return;
    setParams(prev => { const n = new URLSearchParams(prev); n.delete('glAccount'); n.delete('glFrom'); n.delete('glTo'); return n; }, { replace: true });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  return <LedgerAccountsBody ctx={ctx} link={link} />;
}

function LedgerAccountsBody({ ctx, link }: { ctx: AwCtx; link: { accountId: string; from: string; to: string } | null }) {
  const { t } = useTranslation();
  // ── The window: a period, or a custom date range (COA-B-6) ──
  const [mode, setMode] = useState<'period' | 'range'>(link?.from && link?.to ? 'range' : 'period');
  // ⛔ NO PERIOD OF ITS OWN. The period this screen reads is the SHELL's (ctx.periodId), and the
  // picker below drives the shell's (ctx.setPeriod). A second period kept here would let the toolbar
  // say one period while the bar above it says another, and the figures would belong to whichever
  // of them the request happened to carry.
  const periodId = ctx.periodId;
  const [from, setFrom] = useState(link?.from || '');
  const [to, setTo] = useState(link?.to || '');
  // ── The filters (COA-B-3 · B-6) ──
  const [cls, setCls] = useState('');
  // b13 S1 (COA-B-6): the account group — null = every group, '' = the ungrouped accounts, else the stored word. Held
  // here with the class, above the level that comes and goes (COA-B-9), and put into the one `view` below.
  const [group, setGroup] = useState<string | null>(null);
  // ⛔ ON by default, and that is the requirement's own reason: 196 of production's 273 accounts
  // have no posted line at all, so a list that opened on the whole chart would be a wall of zeroes
  // with the 77 accounts somebody came to read buried in it. How many rows the toggles are hiding
  // is printed under the table and the toggle is right there, so nothing is hidden silently.
  const [moved, setMoved] = useState(true);
  const [nonzero, setNonzero] = useState(false);
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  // ── The two levels ──
  const [picked, setPicked] = useState<Picked | null>(null);
  const [sum, setSum] = useState<GlSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [e, setE] = useState('');
  const [classes, setClasses] = useState<string[]>([]);
  const [busy, setBusy] = useState('');
  // The statutory print, on screen, where this menu used to open it — behind a button now.
  const [showBook, setShowBook] = useState(false);
  // ── Batch 12 tail T3 (FS-11, TL-17): level 1's column sort — the SERVER orders the rows inside their class groups
  // (services/ledgerAccounts.ts accountSummary), so the groups keep their headers and the files follow the screen;
  // none at all is the chart's own order, which "Chart order" puts back. Kept per viewer, like every list's: a
  // period change re-keys this pane and the order comes back with it; level 2 is the same mount, so it survives too.
  const ls = useListSort(ctx, 'gl.accounts');
  const { sort: sortK, dir: sortD } = sortArgs(ls);

  // ⛔ The search box goes to the SERVER: it matches the account code, BOTH names, the description, the
  // group and the type (COA-B-5, batch 5 round 6), over the whole chart, which a filter across the rows
  // in hand could not do once a class filter had narrowed them. And because it is the server's filter,
  // the six exits carry it too: a file taken after a search by group holds the accounts the screen
  // showed, and its caption names the term (services/ledgerAccounts.ts ledgerFilter's label).
  useEffect(() => { const h = setTimeout(() => setQd(q), 220); return () => clearTimeout(h); }, [q]);
  // A custom range opens on the period's own dates, so the first request is a valid one.
  useEffect(() => {
    if (mode !== 'range' || from || to) return;
    setFrom(ctx.period?.start_date || ''); setTo(ctx.period?.end_date || '');
  }, [mode, from, to, ctx.period]);

  // ⛔ The five classes come from the SERVER (GET /account-classes) so this screen cannot offer a
  // sixth, and cannot offer one it invented from a code's digits.
  useEffect(() => {
    let alive = true;
    api.ledgerAccountClasses()
      .then((d: any) => { if (alive) setClasses((d?.classes || []).map((x: any) => String(x))); })
      .catch(() => { if (alive) setClasses([]); });
    return () => { alive = false; };
  }, []);

  /** ⛔ ONE OBJECT, SIX EXITS. The list, the detail, and the two export formats of each are all
   *  given THIS — never a second object that happens to look like it. `api.ledgerAcctQs` is the only
   *  place it becomes a query string, and it is the place the period-OR-range rule is kept. */
  const view: LedgerView = useMemo(() => (
    mode === 'period'
      ? { periodId, class: cls, group, moved, nonzero, q: qd, sort: sortK, dir: sortD }
      : { clientId: ctx.clientId, from, to, class: cls, group, moved, nonzero, q: qd, sort: sortK, dir: sortD }
  ), [mode, periodId, ctx.clientId, from, to, cls, group, moved, nonzero, qd, sortK, sortD]);
  const ready = mode === 'period' ? !!periodId : !!(from && to);

  useEffect(() => {
    if (!ready) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    api.ledgerAccounts(view)
      // ⛔ The rows are NOT cleared first. Level 2 is open over them half the time, and COA-B-9's
      // scroll restore needs them in the DOM the instant level 1 comes back.
      .then((d: GlSummary) => { if (alive) { setSum(d); setE(''); } })
      .catch((x: any) => { if (alive) { setSum(null); setE(err(x)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [view, ready, ctx.reload]);

  // ── COA-B-9: the scroll position, restored rather than merely remembered ──
  const atRef = useRef(0);
  const backRef = useRef(false);
  const openAccount = (r: GlAccountRow) => {
    atRef.current = pageY();
    setPicked({ accountId: r.accountId, code: r.code, nameTh: r.nameTh, nameEn: r.nameEn, class: r.class, rollup: r.rollup ?? null, descendants: r.descendants || 0 });
  };
  const back = () => { backRef.current = true; setPicked(null); };
  // The account a link names, read as level 1 reads a row: the head of level 2 is drawn from it. Once, on arrival.
  useEffect(() => {
    if (!link) return undefined;
    let alive = true;
    api.ledgerAccounts({ ...view, accountId: link.accountId })
      .then((d: GlSummary) => { const r = d.rows?.[0]; if (alive && r) openAccount(r); })
      .catch((x: any) => { if (alive) setE(err(x)); });
    return () => { alive = false; };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (picked || !backRef.current) return;
    backRef.current = false;
    // Layout effect, not a plain one: the rows are already in the DOM at this point and the browser
    // has not painted, so the reader never sees the top of the list flash past.
    window.scrollTo(0, atRef.current);
  }, [picked]);

  /** Every slow exit spins (the house's download-button rule) and only one runs at a time. */
  const run = (k: string, go: () => Promise<any>) => {
    if (busy) return;
    setBusy(k); setE('');
    go().catch((x: any) => setE(err(x))).finally(() => setBusy(''));
  };

  // ── The toolbar: ONE row that never wraps (components/PeakToolbar) ──
  // Filters on the left scroll inside their own box; the action menus on the right are measured and
  // whatever does not fit folds into a trailing More. ⛔ Never `flexWrap: 'wrap'` — the owner's rule.
  const clsLabel = cls ? (CLASS_KEY[cls] ? t(CLASS_KEY[cls]) : cls) : t('aw_gl_class_all');
  const grpLabel = group === null ? t('aw_gl_agrp_all') : group === '' ? t('aw_gl_agrp_none') : group;
  const grpWords = (sum?.groupOptions || []).filter(o => o.group !== '');
  const grpNone = (sum?.groupOptions || []).find(o => o.group === '');
  const actions: BarAction[] = [
    {
      key: 'class', label: `${t('aw_gl_class')}: ${clsLabel}`, icon: 'ti ti-category', title: t('aw_gl_class_title'),
      menu: [
        { key: 'all', label: t('aw_gl_class_all'), icon: cls ? undefined : 'ti ti-check', onClick: () => setCls('') },
        ...classes.map(c => ({
          key: c, label: CLASS_KEY[c] ? t(CLASS_KEY[c]) : c, icon: cls === c ? 'ti ti-check' : undefined,
          onClick: () => setCls(c),
        })),
      ],
    },
    {
      // b13 S1 (COA-B-6): the account group, beside the class. Its choices are the SERVER's (`groupOptions`: every
      // word this chart uses, with its count, over the whole chart) — the words as the chart stores them, never
      // translated — and "Not grouped" only when some account has no group. A GET, so every seat has it.
      key: 'group', label: `${t('aw_gl_agrp')}: ${grpLabel}`, icon: 'ti ti-tags', title: t('aw_gl_agrp_title'),
      menu: [
        { key: 'all', label: t('aw_gl_agrp_all'), icon: group === null ? 'ti ti-check' : undefined, onClick: () => setGroup(null) },
        ...grpWords.map(o => ({
          key: `g:${o.group}`, label: o.group, hint: t('aw_gl_agrp_count', { count: o.accounts }),
          icon: group === o.group ? 'ti ti-check' : undefined, onClick: () => setGroup(o.group),
        })),
        ...(grpNone ? [{
          key: 'none', label: t('aw_gl_agrp_none'), hint: t('aw_gl_agrp_count', { count: grpNone.accounts }),
          icon: group === '' ? 'ti ti-check' : undefined, onClick: () => setGroup(''),
        }] : []),
      ],
    },
    {
      // ── Exit 1 of 2: THIS VIEW, with its filters. A working paper, never a statutory book. ──
      key: 'export', label: t('aw_gl_export'), icon: 'ti ti-download', title: t('aw_gl_export_title'),
      busy: busy === 'xlsx' || busy === 'pdf' || busy === 'lines-xlsx' || busy === 'lines-pdf',
      menu: [
        { key: 'xlsx', label: t('aw_gl_export_xlsx'), hint: t('aw_gl_export_hint'), icon: 'ti ti-file-spreadsheet', onClick: () => run('xlsx', () => api.ledgerAccountsXlsx(view)) },
        { key: 'pdf', label: t('aw_gl_export_pdf'), hint: t('aw_gl_export_hint'), icon: 'ti ti-file-type-pdf', onClick: () => run('pdf', () => api.ledgerAccountsPdf(view)) },
        { key: 'lines-xlsx', label: t('aw_gl_export_all_lines'), hint: t('aw_gl_export_all_lines_hint'), icon: 'ti ti-file-spreadsheet', onClick: () => run('lines-xlsx', () => api.ledgerAccountLinesXlsx(view)) },
        { key: 'lines-pdf', label: t('aw_gl_export_all_lines_pdf'), hint: t('aw_gl_export_all_lines_hint'), icon: 'ti ti-file-type-pdf', onClick: () => run('lines-pdf', () => api.ledgerAccountLinesPdf(view)) },
      ],
    },
    {
      // ── Exit 2 of 2: THE STATUTORY BOOK. ⛔ No filter of this screen reaches it: ledgerBookUrl
      // takes the period and nothing else. Different group, different wording, different icon —
      // the rule JournalBook.tsx wrote down for the two exits, obeyed here. The last row opens the
      // same print HTML this menu used to show, on screen, so nothing was taken away.
      key: 'statutory', label: t('aw_gl_statutory'), icon: 'ti ti-book', title: t('aw_gl_statutory_title'),
      busy: busy === 'gl' || busy === 'ar' || busy === 'ap',
      menu: [
        { key: 'gl', label: 'บัญชีแยกประเภท (PDF)', hint: t('aw_gl_statutory_gl'), icon: 'ti ti-book', onClick: () => run('gl', async () => openBlob(await api.ledgerBookUrl('general-ledger', periodId))) },
        { key: 'ar', label: 'บัญชีย่อยลูกหนี้ (PDF)', hint: t('aw_gl_statutory_ar'), icon: 'ti ti-book', onClick: () => run('ar', async () => openBlob(await api.ledgerBookUrl('subsidiary/ar', periodId))) },
        { key: 'ap', label: 'บัญชีย่อยเจ้าหนี้ (PDF)', hint: t('aw_gl_statutory_ap'), icon: 'ti ti-book', onClick: () => run('ap', async () => openBlob(await api.ledgerBookUrl('subsidiary/ap', periodId))) },
        { key: 'show', label: showBook ? t('aw_gl_statutory_hide') : t('aw_gl_statutory_show'), hint: t('aw_gl_statutory_show_hint'), icon: 'ti ti-eye', onClick: () => setShowBook(s => !s) },
      ],
    },
    // The ledger is the chart seen sideways, so the way to fix an account stays reachable from here.
    { key: 'chart', label: t('aw_gl_manage_chart'), icon: 'ti ti-list-numbers', onClick: () => ctx.setNav('accounting', 'chart') },
  ];

  const filters = (
    <>
      <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
        {t('aw_period')}
      </label>
      {/* The period picker, with the custom range as its last option (COA-B-6). One control, so the
          two windows cannot both look chosen — the server refuses a request carrying both. */}
      <select value={mode === 'range' ? '__range__' : periodId} style={{ ...inp, flex: 'none' }}
        aria-label={t('aw_period')}
        onChange={ev => {
          const v = ev.target.value;
          if (v === '__range__') { setMode('range'); return; }
          setMode('period'); ctx.setPeriod(v);
        }}>
        {ctx.periods.map(p => (
          <option key={p.id} value={p.id}>{dmy(p.start_date)} – {dmy(p.end_date)}</option>
        ))}
        <option value="__range__">{t('aw_gl_custom_range')}</option>
      </select>
      {mode === 'range' && (
        <>
          <input type="date" value={from} onChange={ev => setFrom(ev.target.value)} title={t('aw_jl_from')} aria-label={t('aw_jl_from')} style={{ ...inp, flex: 'none' }} />
          <span style={{ color: 'var(--text-tertiary)' }}>–</span>
          <input type="date" value={to} onChange={ev => setTo(ev.target.value)} title={t('aw_jl_to')} aria-label={t('aw_jl_to')} style={{ ...inp, flex: 'none' }} />
        </>
      )}
      <span style={{ width: 1, height: 22, background: 'var(--border-strong)', flex: 'none' }} aria-hidden />
      {/* COA-B-3, the two toggles. Pressed = the rows are hidden. */}
      <button onClick={() => setMoved(m => !m)} aria-pressed={moved} title={t('aw_gl_hide_quiet_title')}
        style={{ ...(moved ? { ...chip, ...chipOn } : chip), flex: 'none', whiteSpace: 'nowrap' }}>
        <i className={moved ? 'ti ti-eye-off' : 'ti ti-eye'} aria-hidden /> {t('aw_gl_hide_quiet')}
      </button>
      <button onClick={() => setNonzero(n => !n)} aria-pressed={nonzero} title={t('aw_gl_hide_zero_title')}
        style={{ ...(nonzero ? { ...chip, ...chipOn } : chip), flex: 'none', whiteSpace: 'nowrap' }}>
        <i className={nonzero ? 'ti ti-eye-off' : 'ti ti-eye'} aria-hidden /> {t('aw_gl_hide_zero')}
      </button>
      <input value={q} onChange={ev => setQ(ev.target.value)} placeholder={t('aw_gl_search_ph')}
        aria-label={t('aw_gl_search_ph')} style={{ ...inp, flex: 'none', width: 240 }} />
    </>
  );

  // The classes, in the server's own order, each with the rows it holds. ⛔ GROUPING ONLY — not one
  // figure is added up here (a per-class subtotal computed on the screen would be a figure the file
  // taken from this screen does not carry, which is exactly the drift rule 5 forbids).
  const groups = useMemo(() => {
    const rows = sum?.rows || [];
    const order = classes.length ? classes : rows.map(r => r.class).filter((c, i, a) => a.indexOf(c) === i);
    const seen = new Set<string>();
    const out: { cls: string; rows: GlAccountRow[] }[] = [];
    for (const c of order) { const rs = rows.filter(r => r.class === c); seen.add(c); if (rs.length) out.push({ cls: c, rows: rs }); }
    // A class the server returned that is not in /account-classes still gets its rows: they are
    // posted money, and dropping them to keep a tidy list would be the one unforgivable tidy-up.
    for (const r of rows) if (!seen.has(r.class)) { seen.add(r.class); out.push({ cls: r.class, rows: rows.filter(x => x.class === r.class) }); }
    return out;
  }, [sum, classes]);

  // b13 S2: a column sort flattens the tree (the server says so: tree.flat); the ids on screen tell a sub-account whose
  // parent is filtered away ("under …").
  const flatTree = !!sum?.tree?.flat;
  const shownIds = new Set((sum?.rows || []).map(r => r.accountId));
  // (b13 review) The indent counts only the ancestors THIS view shows, as the PDF's does (summaryPdfHtml): a sub-account
  // whose parent a filter (search, group, the toggles) left out is not drawn under whatever unrelated row precedes it — it
  // says "under <code>" instead. With every ancestor on screen this is the server's depth, as before. Bounded and
  // cycle-safe like every walk of the tree.
  const shownDepth = useMemo(() => {
    const byId = new Map((sum?.rows || []).map(r => [r.accountId, r]));
    const out = new Map<string, number>();
    for (const r of sum?.rows || []) {
      let d = 0; const seen = new Set<string>([r.accountId]);
      for (let p = byId.get(r.parentId || ''); p && !seen.has(p.accountId); p = byId.get(p.parentId || '')) { seen.add(p.accountId); d++; }
      out.set(r.accountId, d);
    }
    return out;
  }, [sum]);

  if (!ctx.periods.length) return <div style={note}>{t('aw_no_period')}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ ...card, padding: 0, overflow: 'visible' }}>
        <PeakToolbar filters={filters} actions={actions} />
        {picked ? (
          <div style={{ padding: 14 }}>
            <AccountLines ctx={ctx} picked={picked} view={view} onBack={back} onExport={run} busy={busy} exportErr={e} />
          </div>
        ) : (
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={sectionHead}>บัญชีแยกประเภท · {t('aw_gl_title')}</div>
              <div style={note}>{t('aw_gl_two_exits')}</div>
            </div>
            {e && <div style={errBox}>{e}</div>}
            {sum && <OpeningNote o={sum.opening} window={sum.window} />}
            {sum && <OffChartNote o={sum.offChart} />}
            {sum && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {t('aw_gl_counts', { shown: sum.counts.shown, chart: sum.counts.chart, moved: sum.counts.withMovement })}
                {/* ⛔ WHAT THE TOGGLES ARE HIDING, as a number. A list that quietly showed 19 of 35
                    accounts is how an accountant concludes the chart is short. */}
                {sum.counts.hidden > 0 ? ` · ${t('aw_gl_hidden', { count: sum.counts.hidden })}` : ''}
                {sum.window.basis === 'range' ? ` · ${t('aw_gl_range_note')}` : ''}
                {/* Only while a column sort is on: the way back to the chart's own order (TL-17). */}
                {!ls.isDefault && (
                  <button type="button" onClick={ls.reset} data-gl-sort-reset style={{ ...linkBtn, marginLeft: 8, fontSize: 12 }}>
                    <i className="ti ti-list-numbers" aria-hidden /> {t('aw_gl_sort_reset')}
                  </button>
                )}
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              {!sum && loading ? <Loader /> : !sum ? null : !sum.rows.length ? <div style={emptyLine}>{t('aw_gl_none')}</div> : (
                <table style={table}>
                  <thead>
                    <tr>
                      {/* FS-11: the code, the name and the four figures sort; the group / type and the class do not (the
                          class IS the grouping, and the order inside each group is what a sort changes). No arrow while
                          the list is in chart order. */}
                      <SortTh k="code" s={ls} label={t('aw_gl_col_code')} />
                      <SortTh k="account" s={ls} label={t('aw_gl_col_account')} />
                      <th style={th}>{t('aw_gl_col_group_type')}</th>
                      <th style={th}>{t('aw_gl_col_class')}</th>
                      <SortTh k="opening" s={ls} align="right" label={t('aw_gl_col_opening')} />
                      <SortTh k="debit" s={ls} align="right" label={t('aw_gl_col_debit')} />
                      <SortTh k="credit" s={ls} align="right" label={t('aw_gl_col_credit')} />
                      <SortTh k="closing" s={ls} align="right" label={t('aw_gl_col_closing')} />
                    </tr>
                  </thead>
                  {/* COA-B-1: one <tbody> per class, so the five groups keep their own zebra rhythm
                      (global.css's `:has(> thead)` rule does the striping — ⛔ never per table). */}
                  {groups.map(g => (
                    <tbody key={g.cls}>
                      <tr>
                        <td style={{ ...td, background: 'var(--c-gray-50)', fontWeight: 800, fontSize: 11.5, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-secondary)' }} colSpan={8}>
                          {CLASS_KEY[g.cls] ? t(CLASS_KEY[g.cls]) : g.cls}
                          <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: 'var(--text-tertiary)' }}>
                            {' · '}{t('aw_gl_group_accounts', { count: g.rows.length })}
                          </span>
                        </td>
                      </tr>
                      {g.rows.map(r => {
                        // b13 S2 (COA-B-11): the figures a row SHOWS — its roll-up when it has sub-accounts, else its own
                        // (the server's figures either way); its own under them when it has lines of its own. Indented by
                        // the server's depth in chart order; flat under a column sort (B13-20).
                        const s = r.rollup ?? r;
                        // (b13 review) …and under it when it has an own balance too (opening or closing), though no own
                        // line in this window — the PDF's rule (ownLineShown), so the rows reconcile to the foot.
                        const own = !!r.rollup && (r.lines > 0 || r.opening !== 0 || r.closing !== 0);
                        const depth = flatTree ? 0 : (shownDepth.get(r.accountId) || 0);
                        const ownLine = (v: number, dash = false) => (own
                          ? <span data-own style={{ display: 'block', fontSize: 10.5, fontWeight: 400, color: 'var(--text-tertiary)' }}>{dash && !v ? '—' : baht(v)}</span> : null);
                        return (
                        <tr key={r.accountId} data-gl-row={r.code} data-depth={depth}>
                          {/* COA-B-7: the code AND the name open the account — both, because a
                              reader reaches for whichever one their eye landed on. */}
                          {/* (b13 review) paddingLeft is always SET (the td's own 9px at depth 0): dropping it when a row
                              goes back to depth 0 (a sort, a filter) would leave the stale indent — or, in a browser, no
                              padding at all, since it overrides the `padding` shorthand. */}
                          <td style={{ ...td, whiteSpace: 'nowrap', paddingLeft: depth ? 10 + 14 * depth : 9 }}>
                            <button onClick={() => openAccount(r)} title={t('aw_gl_open_account')} style={linkBtn}>
                              {!!depth && <i className="ti ti-corner-down-right" aria-hidden style={{ fontSize: 11, color: 'var(--text-tertiary)', marginRight: 3 }} />}
                              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{r.code}</span>
                            </button>
                          </td>
                          <td style={td}>
                            <button onClick={() => openAccount(r)} title={t('aw_gl_open_account')} style={{ ...linkBtn, textAlign: 'left' }}>
                              {/* Both names the chart holds, neither translated: a statutory Thai
                                  account name IS the name. */}
                              <span style={{ display: 'block', fontWeight: 600 }}>{r.nameTh || r.nameEn}</span>
                              {!!r.nameEn && !!r.nameTh && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{r.nameEn}</span>}
                            </button>
                            {!r.active && <span style={{ ...pillGrey, marginLeft: 6 }}>{t('aw_gl_inactive')}</span>}
                            {/* b13 S2: a parent's badge — the row adds up itself and everything under it. */}
                            {!!r.rollup && (
                              <span data-rollup={r.descendants} title={t('aw_gl_rollup_title')}
                                style={{ ...pillGrey, marginLeft: 6, whiteSpace: 'nowrap' }}>∑ {t('aw_gl_rollup', { count: r.descendants || 0 })}</span>
                            )}
                            {own && <span data-own-note style={{ display: 'block', fontSize: 10.5, color: 'var(--text-tertiary)' }}>{t('aw_gl_rollup_own')}</span>}
                            {/* …a sub-account whose parent this view does not show says where it sits ("under <its code>"). */}
                            {!!r.parentCode && !shownIds.has(r.parentId || '') && (
                              <span data-under={r.parentCode} style={{ display: 'block', fontSize: 10.5, color: 'var(--text-tertiary)' }}>{t('aw_gl_under', { code: r.parentCode })}</span>
                            )}
                            {/* …and a parent stored behind the doors that breaks a rule is said, not hidden. */}
                            {!!r.treeProblem && (
                              <span data-tree-problem={r.treeProblem} style={{ display: 'block', fontSize: 10.5, color: '#92400E' }}>
                                <i className="ti ti-alert-triangle" aria-hidden /> {t('aw_gl_tree_problem')}</span>
                            )}
                          </td>
                          {/* Batch 5 round 6 — the group word and the type word as the chart stores
                              them (lang="en": the customer's words, untranslated in every language),
                              the badge the SERVER's kind gives the type, and the Control badge when the
                              account controls a subsidiary ledger. An account nobody has classified
                              shows nothing here — that is the truth about it, not a gap. */}
                          <td data-group-type style={{ ...td, whiteSpace: 'nowrap' }}>
                            {!!r.group && <span data-group lang="en" style={{ color: 'var(--text-secondary)', marginRight: 6 }}>{r.group}</span>}
                            <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                              <TypeBadge type={r.type} kind={r.kind} />
                              <ControlBadge role={r.control} />
                            </span>
                          </td>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                            {CLASS_KEY[r.class] ? t(CLASS_KEY[r.class]) : r.class}
                            {/* The server's own `normalSide`, printed — not a side worked out here. */}
                            <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }} title={t(r.normalSide === 'dr' ? 'aw_gl_normal_dr' : 'aw_gl_normal_cr')}>
                              {' '}{r.normalSide === 'dr' ? t('aw_gl_dr_short') : t('aw_gl_cr_short')}
                            </span>
                          </td>
                          {/* COA-B-2, the four figures, every one of them the server's own SUM over
                              journal_lines' integer satang. `baht` is the one formatter. */}
                          <td style={{ ...num, color: s.opening ? undefined : 'var(--text-tertiary)' }}>{baht(s.opening)}{ownLine(r.opening)}</td>
                          <td style={{ ...num, color: s.dr ? undefined : 'var(--text-tertiary)' }}>{s.dr ? baht(s.dr) : '—'}{ownLine(r.dr, true)}</td>
                          <td style={{ ...num, color: s.cr ? undefined : 'var(--text-tertiary)' }}>{s.cr ? baht(s.cr) : '—'}{ownLine(r.cr, true)}</td>
                          <td style={{ ...num, fontWeight: 600, color: s.closing ? undefined : 'var(--text-tertiary)' }}>{baht(s.closing)}{ownLine(r.closing)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  ))}
                  <tfoot>
                    <tr>
                      <td style={{ ...td, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }} colSpan={4}>
                        {t('aw_gl_total_shown', { count: sum.totals.accounts })}
                      </td>
                      <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }}>{baht(sum.totals.opening)}</td>
                      <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }}>{baht(sum.totals.dr)}</td>
                      <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }}>{baht(sum.totals.cr)}</td>
                      <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }}>{baht(sum.totals.closing)}</td>
                    </tr>
                    {/* ⛔ The trial-balance tie is claimed ONLY over the whole chart. With a class
                        filter, a search or either toggle on, the two sides of a narrowed ledger have
                        no reason to agree, and saying "debits equal credits" there would be a claim
                        about the books made from a claim about a filter. The server decides which
                        sentence applies (`coversWholeChart`), not this screen. */}
                    <tr>
                      <td style={{ ...td, borderBottom: 'none' }} colSpan={8}>
                        {!sum.totals.coversWholeChart
                          ? <span style={note}>{t('aw_gl_partial_total')}</span>
                          : sum.totals.balanced
                            ? <span style={{ fontSize: 11.5, color: '#166534', fontWeight: 700 }}><i className="ti ti-check" aria-hidden /> {t('aw_gl_balanced')}</span>
                            : <span style={{ fontSize: 11.5, color: '#B42318', fontWeight: 700 }}><i className="ti ti-alert-triangle" aria-hidden /> {t('aw_gl_unbalanced', { diff: baht(Math.abs(sum.totals.dr - sum.totals.cr)) })}</span>}
                      </td>
                    </tr>
                    {/* b13 S2 (B13-18): when a ∑ row is on screen, the foot says the totals still count each line once
                        (they are the server's sum of each account's OWN figures). */}
                    {sum.rows.some(r => !!r.rollup) && (
                      <tr>
                        <td data-total-once style={{ ...td, borderBottom: 'none' }} colSpan={8}><span style={note}>∑ {t('aw_gl_total_once')}</span></td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              )}
            </div>
            <div style={{ ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <i className="ti ti-info-circle" style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} aria-hidden />
              <div style={note}>{t('aw_gl_posted_only')}</div>
            </div>
            <ReadOnlyNote />
          </div>
        )}
      </div>
      {/* The statutory print itself, exactly as this menu used to open it — behind the button now.
          ⛔ Its query is the period and `format=html`, the same two parameters it always had: not one
          filter of this screen reaches it. */}
      {showBook && !picked && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={sectionHead}>บัญชีแยกประเภท · {t('aw_gl_statutory')}</div>
            <div style={{ flex: 1 }} />
            <button onClick={() => setShowBook(false)} style={btn}><i className="ti ti-x" aria-hidden /> {t('aw_gl_statutory_hide')}</button>
          </div>
          <div style={note}>{t('aw_gl_statutory_unfiltered')}</div>
          <HtmlViewer base="ledger-books" path="general-ledger" qs={{ periodId }} reload={ctx.reload} />
        </div>
      )}
    </div>
  );
}

/** A cell that opens something, dressed as the text it sits on rather than as a button. */
const linkBtn: React.CSSProperties = {
  border: 'none', background: 'transparent', padding: 0, margin: 0, font: 'inherit',
  color: 'var(--accent-text)', cursor: 'pointer', textAlign: 'inherit' as any,
};

export default LedgerAccountsView;
